import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "../../common.js";

export const createApprTemp1 = async (apprData, startPage) => {
    const style = apprData.style || {};
    const sections = [];

    const SPACER_HEIGHT = 100;
    const BODY_HEIGHT = 14570;

    const MAX_COL_HEIGHT = BODY_HEIGHT - 200;
    const COL_WIDTH = 5273;
    const GAP_WIDTH = 750;
    const SIDE_PAD = 300;
    const pointMultiplier = 21;
    const HEADING_EST_HEIGHT = (style.label?.size || 14) * pointMultiplier + 120;
    const LINE_EST_HEIGHT = (style.text?.size || 11) * pointMultiplier;
    const fontSize = style.fontSize || 22;
    const CHARS_PER_LINE = Math.floor(1250 / fontSize);

    // Initial item queue
    let itemQueue = [...(apprData.questions || [])];

    if (!itemQueue.length) return [];

    let currentAbsPage = startPage || 1;

    while (itemQueue.length > 0) {
        const leftColChildren = [];
        const rightColChildren = [];

        const createQuestionPart = (q, responseText) => {
            const children = [];
            // Only add heading if it's the start of a question
            if (q.heading && !q._isContinuation) {
                children.push(new Paragraph({
                    spacing: { after: 120 },
                    children: [
                        new TextRun({
                            text: q.heading || "",
                            color: fixColor(style.label?.color || "#000000"),
                            size: (style.label?.size || 14) * SCALE,
                            font: style.label?.font,
                            bold: style.label?.bold,
                            italics: style.label?.italic,
                            underline: style.label?.underline,
                        }),
                    ],
                }));
            } else if (q.heading && q._isContinuation) {
                // Optional: minimalist header for continuation if desired, or skip
            }

            children.push(new Paragraph({
                spacing: { after: 300 },
                children: [
                    new TextRun({
                        text: responseText || "",
                        color: fixColor(style.text?.color || "#333333"),
                        size: (style.text?.size || 11) * SCALE,
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline,
                    }),
                ],
            }));
            return children;
        };

        const fillColumn = (remainingHeight) => {
            const colItems = [];
            let currentHeight = 0;

            while (itemQueue.length > 0) {
                const q = itemQueue[0];
                const headingH = (q._isContinuation) ? 0 : HEADING_EST_HEIGHT;
                const response = q.response || "";
                const responseLines = Math.ceil(response.length / CHARS_PER_LINE) || 1;
                const responseH = (responseLines * LINE_EST_HEIGHT) + 300;
                const totalItemH = headingH + responseH;

                if (currentHeight + totalItemH <= remainingHeight) {
                    // Full item fits
                    colItems.push(...createQuestionPart(q, response));
                    currentHeight += totalItemH;
                    itemQueue.shift();
                } else {
                    // Item doesn't fit fully. Can we split the response?
                    const availableForResponse = remainingHeight - currentHeight - headingH - 300; // 300 for spacing
                    if (availableForResponse > LINE_EST_HEIGHT * 2) { // Minimum 2 lines to bother splitting
                        const linesThatFit = Math.floor(availableForResponse / LINE_EST_HEIGHT);
                        const charsThatFit = linesThatFit * CHARS_PER_LINE;

                        // Find a good place to split (word boundary)
                        let splitIndex = charsThatFit;
                        const lastSpace = response.lastIndexOf(' ', splitIndex);
                        if (lastSpace > charsThatFit * 0.7) splitIndex = lastSpace;

                        const firstPart = response.substring(0, splitIndex).trim();
                        const secondPart = response.substring(splitIndex).trim();

                        if (firstPart) {
                            colItems.push(...createQuestionPart(q, firstPart));
                            currentHeight += headingH + (linesThatFit * LINE_EST_HEIGHT) + 300;

                            // Re-queue the rest
                            itemQueue[0] = {
                                ...q,
                                heading: q.heading,
                                response: secondPart,
                                _isContinuation: true
                            };
                        }
                    }
                    // Either it split or it was too small to split, break to move to next column
                    break;
                }
            }
            return { items: colItems, height: currentHeight };
        };

        // Fill Left Column
        const leftRes = fillColumn(MAX_COL_HEIGHT);
        leftColChildren.push(...leftRes.items);
        let leftColHeight = leftRes.height;

        // Fill Right Column
        const rightRes = fillColumn(MAX_COL_HEIGHT);
        rightColChildren.push(...rightRes.items);
        let rightColHeight = rightRes.height;

        const headerTable = createCommonHeader(apprData.title, fixColor(style.colorScheme?.header || "#545454"));
        const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

        // 2. BODY TABLE
        const bodyTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            layout: TableLayoutType.FIXED,
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({
                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({ width: { size: SIDE_PAD, type: WidthType.DXA }, children: [] }),
                        new TableCell({
                            width: { size: COL_WIDTH, type: WidthType.DXA },
                            children: leftColChildren,
                        }),
                        new TableCell({ width: { size: GAP_WIDTH, type: WidthType.DXA }, children: [] }),
                        new TableCell({
                            width: { size: COL_WIDTH, type: WidthType.DXA },
                            children: rightColChildren,
                        }),
                        new TableCell({ width: { size: SIDE_PAD, type: WidthType.DXA }, children: [] }),
                    ],
                }),
            ],
        });

        sections.push({
            properties: {
                page: {
                    margin: { top: 0, right: 0, bottom: 0, left: 0 },
                },
            },
            children: [
                headerTable,
                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                bodyTable,
                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                footerTable
            ],
        });

        currentAbsPage++;
        if (leftColChildren.length === 0 && rightColChildren.length === 0) break;
    }

    return sections;
};
